import { 
    Typography, 
    Paper, 
    Stack, 
    Chip, 
    Divider, 
    Box,
    Link,
    Grid
} from '@mui/material';
import { 
    Business, 
    CalendarToday, 
    Person, 
    Code, 
    Launch,
    Key,
    TrendingUp
} from '@mui/icons-material';
import React from 'react';
import VideoView from '../video/VideoView';
import GalleryView from '../gallery/GalleryView';

export default function ProjectView(props) {
    const parseListItems = (text) => {
        if (!text) return [];
        return text.split(',').filter(item => item.trim()).map(item => item.trim());
    };

    const parseLinks = (linksText) => {
        if (!linksText) return [];
        return linksText.split('\n').filter(link => link.trim()).map(link => {
            const trimmed = link.trim();
            if (trimmed.includes('http')) {
                return { url: trimmed, label: trimmed.replace(/https?:\/\//, '') };
            }
            return { url: `https://${trimmed}`, label: trimmed };
        });
    };

    const industryTags = props.industry ? props.industry.split(',').map(tag => tag.trim()) : [];
    const toolsList = parseListItems(props.tools);
    const contributionsList = parseListItems(props.contributions);
    const resultsList = parseListItems(props.results);
    const linksList = parseLinks(props.links);

    return (
        <Paper elevation={4} sx={{ p: 4, maxWidth: 1000, mx: 'auto' }}>
            <Stack spacing={4}>
                {/* Header Section */}
                <Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                        {props.name}
                    </Typography>

                    <Typography variant="h5" component="h5" gutterBottom fontWeight="bold">
                        {props.subtitle}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2} useFlexGap={true}>
                        <Chip 
                            label={props.project_type} 
                            color="primary" 
                            variant="filled"
                            size="medium"
                        />
                        {props.years && (
                            <Chip 
                                icon={<CalendarToday fontSize='small' sx={{paddingLeft:.5}}/>}
                                label={props.years}
                                variant="outlined"
                            />
                        )}
                        {props.client && (
                            <Chip 
                                icon={<Business fontSize='small' sx={{paddingLeft:.5}}/>}
                                label={props.client}
                                variant="outlined"
                            />
                        )}
                        {props.role && (
                            <Chip 
                                icon={<Person fontSize='small' sx={{paddingLeft:.5}}/>}
                                label={props.role}
                                variant="outlined"
                            />
                        )}
                    </Stack>

                    {/* INSERT GALLERY HERE */}
                    <VideoView {...props} />
                    {props.photos && props.photos.length > 0 && (<GalleryView photos={props.photos} />)}
                </Box>

                <Divider />

                {/* Summary Section */}
                {props.summary && (
                    <Box>
                        <Typography variant="h5" gutterBottom fontWeight="medium">
                            Overview
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                            {props.summary}
                        </Typography>
                    </Box>
                )}

                {/* Two Column Layout for Details */}
                <Grid container spacing={0}>
                    {/* Left Column */}
                    <Grid item xs={12} md={6}>
                        {/* Contributions */}
                        {contributionsList.length > 0 && (
                            <Box mb={4}>
                                <Typography variant="h5" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Key color="primary" />
                                    Key Contributions
                                </Typography>
                                <Stack spacing={1}>
                                    {contributionsList.map((contribution, index) => (
                                        <Typography 
                                            key={index} 
                                            variant="body2" 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start',
                                                '&:before': {
                                                    content: '"•"',
                                                    color: 'primary.main',
                                                    fontWeight: 'bold',
                                                    marginRight: 1,
                                                    marginTop: 0
                                                }
                                            }}
                                        >
                                            {contribution}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={6} sx={{ paddingLeft: { md: 4 }}}>
                        {/* Results */}
                        {resultsList.length > 0 && (
                            <Box mb={4}>
                                <Typography variant="h5" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TrendingUp color="primary" />
                                    Results & Impact
                                </Typography>
                                <Stack spacing={1}>
                                    {resultsList.map((result, index) => (
                                        <Typography 
                                            key={index} 
                                            variant="body2" 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start',
                                                '&:before': {
                                                    content: '"→"',
                                                    color: 'success.main',
                                                    fontWeight: 'bold',
                                                    marginRight: 1,
                                                    marginTop: 0
                                                }
                                            }}
                                        >
                                            {result}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        
                    </Grid>
                    {/* Skills & Tools */}
                        {toolsList.length > 0 && (
                            <Box>
                                <Typography variant="h5" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Code color="primary" />
                                    Skills & Tools
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap={true}>
                                    {toolsList.map((tool, index) => (
                                        <Chip 
                                            key={index}
                                            label={tool}
                                            variant="outlined"
                                            size="small"
                                            sx={{ mb: 1 }}
                                        />
                                    ))}
                                </Stack>
                                <Typography variant="body1">‎ </Typography>
                            </Box>
                        )}
                    {/* Links */}
                        {linksList.length > 0 && (
                            <Box>
                                <Typography variant="h5" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Launch color="primary" />
                                    Project Links
                                </Typography>
                                <Stack spacing={1}>
                                    {linksList.map((linkObj, index) => (
                                        <Link 
                                            key={index}
                                            href={linkObj.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                             {linkObj.label}
                                        </Link>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                </Grid>
            </Stack>
        </Paper>
    );
}


